import { Seo } from "../components/Seo";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHero } from "../components/PageHero";
import { VG_ORANGE } from "../lib/brand";

const ZONES = [
  {
    num: "1",
    label: "Zone 1 — Exterior",
    title: "Stop Contaminants at the Door",
    text: "The first line of defense starts before visitors even enter your building. Zone 1 products are engineered to aggressively scrape and remove the heaviest soil, debris, and moisture from footwear, preventing up to 80% of tracked-in contaminants from ever reaching your interior floors.",
    products: ["Entry Grid Systems", "Outdoor Mats", "Scraper Mats"],
    cta: "View Entrance Matting",
    to: "/products?category=entry-grid-systems,custom-logos,commercial-mats",
    testId: "zone-1",
  },
  {
    num: "2",
    label: "Zone 2 — Transition",
    title: "The Critical Second Barrier",
    text: "Zone 2 acts as your transition area — the airlock between exterior conditions and your pristine interior. These products continue the scraping process while adding superior moisture absorption. This critical zone captures what Zone 1 missed and prepares visitors for entry.",
    products: ["Entrance Carpet Tiles", "Entry Mats", "Modular Systems"],
    cta: "View Defense Flooring",
    to: "/products?category=carpet-tile-goods,entry-grid-systems,custom-logos",
    testId: "zone-2",
  },
  {
    num: "3",
    label: "Zone 3 — Interior",
    title: "Where Performance Meets Design",
    text: "The final line of defense where function meets aesthetics. Zone 3 products are designed to capture any remaining particles while delivering the visual appeal your brand deserves. From luxury vinyl to premium carpet, these solutions enhance your space while protecting your investment.",
    products: ["Modular Carpet Tile", "Broadloom Goods", "Luxury Vinyl Planks & Tiles"],
    cta: "View Interior Flooring",
    to: "/products?category=broadloom-sheet-roll-goods,carpet-tile-goods,luxury-vinyl-plank-tiles",
    testId: "zone-3",
  },
];

export default function EntranceDefense() {
  const { hash } = useLocation();

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace("#", "");
    // Wait for content to paint (and after ScrollToTop runs) before scrolling.
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(timer);
  }, [hash]);

  return (
    <div data-testid="entrance-defense-page">
      <Seo title="Entrance Defense" description="A complete three-zone entrance matting strategy that scrapes, wipes, and dries foot traffic — stopping up to 80% of tracked-in dirt before it reaches your floors." />
      <PageHero
        title="Entrance Defense"
        subtitle="A complete entrance matting strategy that scrapes, wipes, and dries foot traffic before it ever reaches your floors."
        testId="entrance-hero"
      />

      {/* Zone Defense intro + diagram */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: VG_ORANGE }}>
              Zone Defense
            </span>
            <h2 className="mt-2 font-heading text-3xl sm:text-4xl font-extrabold text-neutral-900">
              A three-zone system for total floor protection
            </h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Our comprehensive three-zone system provides complete protection for your commercial
              floors — from the moment visitors arrive through their journey into your space.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              {ZONES.map((z) => (
                <a
                  key={z.num}
                  href={`#${z.testId}`}
                  className="flex items-center gap-2 rounded-lg border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:border-[#ED8B00] hover:text-[#ED8B00]"
                >
                  <span
                    className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white"
                    style={{ backgroundColor: VG_ORANGE }}
                  >
                    {z.num}
                  </span>
                  Zone {z.num}
                </a>
              ))}
            </div>
          </div>
          <div className="overflow-hidden rounded-2xl">
            <img
              src="/products/entrance-defense/zone-system.png"
              alt="Three-zone entrance defense system diagram"
              className="h-full w-full object-contain"
              data-testid="zone-diagram"
            />
          </div>
        </div>
      </section>

      {/* Zone detail windows */}
      <section className="bg-neutral-50 py-16">
        <div className="mx-auto max-w-7xl space-y-8 px-4">
          {ZONES.map((z, i) => (
            <div
              key={z.num}
              id={z.testId}
              data-testid={`${z.testId}-window`}
              className="scroll-mt-24 grid items-center gap-8 rounded-2xl border border-neutral-200 bg-white p-8 shadow-sm lg:grid-cols-[auto_1fr_auto] lg:gap-12"
            >
              {/* Number badge */}
              <div className="flex items-center gap-4 lg:flex-col lg:items-start">
                <span
                  className="flex h-16 w-16 items-center justify-center rounded-2xl font-heading text-2xl font-extrabold text-white"
                  style={{ backgroundColor: VG_ORANGE }}
                >
                  {z.num}
                </span>
                <span className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  {z.label}
                </span>
              </div>

              {/* Content */}
              <div>
                <h3 className="font-heading text-2xl font-extrabold text-neutral-900">{z.title}</h3>
                <p className="mt-3 text-neutral-600 leading-relaxed">{z.text}</p>
              </div>

              {/* Products + CTA */}
              <div className="lg:w-64">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
                  Recommended Products
                </h4>
                <ul className="mt-3 space-y-2">
                  {z.products.map((p) => (
                    <li key={p} className="flex items-start gap-2 text-sm text-neutral-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0" style={{ color: VG_ORANGE }} />
                      {p}
                    </li>
                  ))}
                </ul>
                <Button
                  asChild
                  className="mt-5 w-full text-white hover:opacity-90"
                  style={{ backgroundColor: VG_ORANGE }}
                  data-testid={`${z.testId}-cta`}
                >
                  <Link to={z.to}>
                    {z.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Why it matters */}
      <section className="mx-auto max-w-5xl px-4 py-16">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 md:p-10 shadow-sm">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-neutral-900">
            Why Entrance Defense matters
          </h2>
          <p className="mt-3 text-neutral-600 leading-relaxed">
            Up to 80% of dirt inside a building is tracked in through the entrance. A properly
            specified matting system protects your investment, keeps interiors cleaner, and reduces
            long-term maintenance costs. Explore our entrance matting products or contact us to build
            the right system for your facility.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Button asChild className="text-white hover:opacity-90" style={{ backgroundColor: VG_ORANGE }} data-testid="entrance-products-cta">
              <Link to="/products?category=entry-grid-systems,commercial-mats,custom-logos">
                Explore Entrance Matting <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" data-testid="entrance-contact-cta">
              <Link to="/contact">Contact Customer Service</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
